import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import Icon from 'components/Icon';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { TABLET_LANDSCAPE_BREAKPOINT } from 'helpers/BreakpointHelper';
import { isEpgSearchEnabled } from 'helpers/ConfigHelper';
import {
  isBefore,
  isBetween,
  midNightTonightTime,
  now,
  tonight7PMTime,
  tonight830PMTime,
} from 'helpers/DateTimeHelper';
import { Events, handleAnalytics, setPagePropsStorage } from '@foxcorp/delta-web-analytics';
import { isDayAfter, isToday, isTomorrow } from '../../LiveHelpers';

import Style from './FilterSearchBar.module.scss';

const {
  epg_dayAfterFilterButton,
  epg_liveNowFilterButton,
  epg_tomorrowHeader,
  epg_tonightFilterButton,
  search_inputPlaceholder,
} = window.Language;

const FilterSearchBar = ({
  atStartDate,
  atEndDate,
  onFieldChange,
  onClearSearch,
  gridNext,
  gridPrev,
  gridStart,
  gridTonight,
  gridTomorrow,
  gridDayAfter,
  searchInput,
  selectedDate,
}) => {
  const [focusSearch, setFocusSearch] = useState(false);
  const [trackEventCalled, setTrackEventCalled] = useState(false);

  if (!isEpgSearchEnabled()) return null;

  const setFocus = (val, clear) => {
    if (!val && document.hasFocus()) {
      setTrackEventCalled(false);
    }
    if (val && !trackEventCalled) {
      handleAnalytics({
        callback: setPagePropsStorage,
        event: Events.SEARCH_STARTED,
        tealiumPayload: {
          ...getCommonTealiumProps(),
        },
      });

      setTrackEventCalled(true);
    }
    setFocusSearch(val);
    if (clear) {
      onClearSearch();
    }
  };

  const focusClass = focusSearch ? Style.focusSearch : null;
  const inputClass = searchInput?.length > 0 ? Style.inputTyped : null;
  const activeClass = Style.active;
  const disabledClass = Style.disabled;
  const currentBreakpoint = _.get(window, 'innerWidth');

  return (
    <div className={classNames(Style.filterInputContainer, focusClass)}>
      <div className={Style.btnWrap}>
        <div className={Style.filterContainer}>
          <button
            className={classNames(Style.filterButtons, atStartDate && activeClass)}
            onClick={() => gridStart()}
          >
            {epg_liveNowFilterButton}
          </button>
          {isToday(now()) && isBefore(new Date(now()).getTime(), tonight830PMTime(now())) ? (
            <button
              className={classNames(
                Style.filterButtons,
                isToday(selectedDate) &&
                  isBetween(selectedDate, tonight7PMTime(selectedDate), midNightTonightTime) && [
                    activeClass,
                    disabledClass,
                  ],
              )}
              onClick={() => gridTonight()}
            >
              {epg_tonightFilterButton}
            </button>
          ) : null}
          {isDayAfter(selectedDate) && atEndDate ? null : (
            <button
              className={classNames(Style.filterButtons, isTomorrow(selectedDate) && activeClass)}
              onClick={() => gridTomorrow()}
            >
              {epg_tomorrowHeader}
            </button>
          )}
          {isDayAfter(selectedDate) && atEndDate ? null : (
            <button
              className={classNames(Style.filterButtons, isDayAfter(selectedDate) && activeClass)}
              onClick={() => gridDayAfter()}
            >
              {epg_dayAfterFilterButton}
            </button>
          )}
        </div>
        <div className={Style.searchInputBox}>
          <button className={Style.searchIconButton} onClick={() => setFocus(true)}>
            <Icon className={Style.searchIcon} icon="search" />
          </button>
          <>
            <input
              type="text"
              id="searchEPG"
              name="searchSchedule"
              placeholder={search_inputPlaceholder}
              onChange={onFieldChange}
              onFocus={() => setFocus(true)}
              onBlur={() =>
                currentBreakpoint >= TABLET_LANDSCAPE_BREAKPOINT ? setFocus(false) : null
              }
            />
            <button
              className={classNames(Style.closeIconButton, inputClass)}
              onMouseDown={() => onClearSearch()}
            >
              <Icon icon="close" />
            </button>
          </>
        </div>
      </div>
      <div className={Style.arrowContainer}>
        <span
          onClick={() => (!atStartDate ? gridPrev() : null)}
          className={classNames(Style.arrowIcon, atStartDate && disabledClass)}
        >
          <Icon
            color={atStartDate ? 'white25' : 'white50'}
            size="medium"
            viewBox="0 2 23 32"
            icon="ios-arrow-left"
          />
        </span>
        <span
          onClick={() => (!atEndDate ? gridNext() : null)}
          className={classNames(Style.arrowIcon, atEndDate && disabledClass)}
        >
          <Icon
            color={atEndDate ? 'white25' : 'white50'}
            size="medium"
            viewBox="0 2 15 32"
            icon="ios-arrow-right"
          />
        </span>
      </div>
    </div>
  );
};

FilterSearchBar.propTypes = {
  atStartDate: PropTypes.bool,
  atEndDate: PropTypes.bool,
  gridNext: PropTypes.func,
  gridPrev: PropTypes.func,
  gridStart: PropTypes.func,
  gridTonight: PropTypes.func,
  gridTomorrow: PropTypes.func,
  gridDayAfter: PropTypes.func,
  onClearSearch: PropTypes.func,
  onFieldChange: PropTypes.func,
  searchInput: PropTypes.string,
  selectedDate: PropTypes.instanceOf(Date),
};

export default React.memo(FilterSearchBar);
